/**
 * Known video provider domains that should be paused when an element is hidden.
 */
const VIDEO_PROVIDER_DOMAINS = ['youtube.com', 'youtu.be', 'vimeo.com', 'dailymotion.com', 'facebook.com'];

/**
 * Check if an iframe src contains a known video provider domain.
 *
 * @param {string} src The iframe src attribute value.
 * @returns {boolean} True if the src contains a video provider domain.
 */
const isVideoIframe = (src: string): boolean => {
  if (!src || '' === src) {
    return false;
  }

  return VIDEO_PROVIDER_DOMAINS.some(domain => src.includes(domain));
};

/**
 * Remove autoplay parameter from iframe src URL to prevent autoplay when restoring.
 *
 * @param {string} src The iframe src URL.
 * @returns {string} The src URL with autoplay parameter removed.
 */
const removeAutoplayFromSrc = (src: string): string => {
  if (!src || '' === src) {
    return src;
  }

  try {
    const url = new URL(src);
    url.searchParams.delete('autoplay');
    return url.toString();
  } catch {
    // If URL parsing fails, use regex to remove autoplay parameter.
    let result = src.replace(/[?&]autoplay=\d+/gi, '').replace(/[?&]autoplay/gi, '');
    // If autoplay was the first parameter, we may have a leading & that should be ?
    if (result.startsWith('&')) {
      result = `?${result.substring(1)}`;
    }
    // Clean up any trailing &
    result = result.replace(/&$/, '');
    return result;
  }
};

/**
 * Pause all playing media within a container element.
 * - Pauses all <video> elements (except section background videos).
 * - Stops playback for <iframe> elements containing known video providers by blanking and restoring their src.
 *
 * @param {HTMLElement} element The container element to search for media within.
 */
export const pauseMedia = (element: HTMLElement): void => {
  if (!element) {
    return;
  }

  try {
    // Pause all <video> elements, excluding section background videos.
    const videoElements = element.querySelectorAll('video');
    if (videoElements.length > 0) {
      videoElements.forEach(video => {
        // Skip videos that are inside section video backgrounds.
        const isSectionBackgroundVideo = video.closest('.et_pb_section_video_bg') !== null;
        if (!isSectionBackgroundVideo) {
          video.pause();
        }
      });
    }

    // Stop playback for iframes containing video providers by blanking and restoring src.
    const iframeElements = element.querySelectorAll('iframe');
    if (iframeElements.length > 0) {
      iframeElements.forEach(iframe => {
        const src = iframe.getAttribute('src');
        if (isVideoIframe(src || '')) {
          const savedSrc = src;
          // Blank the src to stop playback.
          iframe.setAttribute('src', '');
          // Restore src after delay to allow blank navigation to complete.
          // Remove autoplay parameter to prevent video from restarting when restored.
          // This ensures the browser processes the blank navigation before restoring,
          // which stops iframe video playback.
          if (savedSrc) {
            const srcWithoutAutoplay = removeAutoplayFromSrc(savedSrc);
            setTimeout(() => {
              iframe.setAttribute('src', srcWithoutAutoplay);
            }, 100);
          }
        }
      });
    }
  } catch (error) {
    // Silently catch any errors to never break the hide/show interaction flow.
    // Media pausing is a best-effort operation and should not prevent elements from being hidden.
  }
};
